import React, { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const CYCLE_MS = 8_000;

const KIND_LABEL: Record<string, string> = {
  defacement: 'Site defaced',
  leak_site_post: 'Leak site post',
  headline: 'Headline',
  social_post: 'Social reaction',
};

// Cycles through the exercise's public-world artefacts (defacement, leak-site
// posts, headlines, social reaction) — the "outside world reacting" content,
// distinct from the breaking-news bulletin (BreakingNewsOverlay), which is a
// full-bleed takeover rather than a grid widget.
const PublicWorldWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const artefacts = state?.tabletop?.artefacts ?? [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (artefacts.length <= 1) return;
    const interval = setInterval(() => setIndex(i => (i + 1) % artefacts.length), CYCLE_MS);
    return () => clearInterval(interval);
  }, [artefacts.length]);

  if (artefacts.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        <Globe size={16} className="mr-2 opacity-40" /> No public-world activity yet
      </div>
    );
  }

  const current = artefacts[index % artefacts.length];

  return (
    <div className="relative flex flex-col h-full overflow-hidden rounded-lg">
      {current.url ? (
        <img src={current.url} alt={current.text || current.kind} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-black/40" />
      )}
      <div className="relative mt-auto bg-gradient-to-t from-black/85 to-transparent p-3">
        <p className="text-sm uppercase tracking-wide text-primary font-bold">{KIND_LABEL[current.kind] || current.kind}</p>
        <p className="text-sm text-white leading-snug">{current.lowerThird || current.text}</p>
      </div>
      {artefacts.length > 1 && (
        <div className="absolute top-2 right-2 flex gap-1">
          {artefacts.map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === index ? 'bg-primary' : 'bg-white/30'}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicWorldWidget;
